/**
 * Pack itinerary day tiles onto US Letter pages.
 * A tile that does not fit the remaining page starts a continuation page.
 */

export const PDF_PAGE = {
  width: 612,
  height: 792,
  margin: 50,
  tileSpacing: 12,
  continuationHeaderHeight: 28,
  lineHeight: 14,
  tilePadding: 16,
} as const;

export const PDF_CONTENT_HEIGHT = PDF_PAGE.height - PDF_PAGE.margin * 2;

export function estimateTileHeight(lineCount: number): number {
  return PDF_PAGE.tilePadding + Math.max(lineCount, 1) * PDF_PAGE.lineHeight;
}

export function wrapPdfText(text: string, maxChars = 78): string[] {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  const words = clean.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
}

export interface PackedTilePage<T> {
  /** Set when this page continues a day that started earlier. */
  continuationTitle: string | null;
  tiles: T[];
}

export function packDayTiles<T extends { height: number }>(
  days: Array<{ continuationTitle: string; tiles: T[] }>,
  layout: {
    contentHeight: number;
    tileSpacing: number;
    continuationHeaderHeight: number;
  } = {
    contentHeight: PDF_CONTENT_HEIGHT,
    tileSpacing: PDF_PAGE.tileSpacing,
    continuationHeaderHeight: PDF_PAGE.continuationHeaderHeight,
  }
): PackedTilePage<T>[] {
  const pages: PackedTilePage<T>[] = [];

  for (const day of days) {
    if (day.tiles.length === 0) continue;

    let page: PackedTilePage<T> = { continuationTitle: null, tiles: [] };
    let used = 0;

    const pushPage = (): void => {
      if (page.tiles.length === 0) return;
      pages.push(page);
    };

    const startContinuation = (): void => {
      pushPage();
      page = { continuationTitle: day.continuationTitle, tiles: [] };
      used = layout.continuationHeaderHeight;
    };

    for (const tile of day.tiles) {
      const gap = page.tiles.length > 0 ? layout.tileSpacing : 0;
      if (page.tiles.length > 0 && used + gap + tile.height > layout.contentHeight) {
        startContinuation();
      }
      const placedGap = page.tiles.length > 0 ? layout.tileSpacing : 0;
      page.tiles.push(tile);
      used += placedGap + tile.height;
    }

    pushPage();
  }

  return pages;
}
